import React, { useContext } from "react";
import { Grid, Card, CardContent, Typography, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboard,
  faClipboardCheck,
  faUsers,
  faChartPie,
} from "@fortawesome/free-solid-svg-icons";
import Resources from "./Resources";
import CoordinatorOfPathways from "./CoordinatorOfPathways";
import CoordinatorOfOperations from "./CoordinatorOfOperations";
import Announcements from "./Announcements";
import { SchoolContext } from "../../../config/SchoolContext";
import useAppsTesting from "../../CcpSchool/CcpSchoolAppsAndTesting/hooks/AppsTestingHook";
import useRestrictedReportsHook from "../../CcpSchool/CcpSchoolRestrictedCourses/hooks/RestrictedReportsHook";

const quickLinks = [
  {
    label: "Course Planning",
    icon: faClipboard,
    path: "/CcpSchoolCoursePlanning",
    cardBg: "#eef2ff",
    iconBg: "#c7d2fe",
    iconColor: "#4f46e5",
    textColor: "#4f46e5",
  },
  {
    label: "Application Approval",
    icon: faClipboardCheck,
    external: "applicationApproval",
    cardBg: "#fffbeb",
    iconBg: "#fde68a",
    iconColor: "#d97706",
    textColor: "#d97706",
  },
  {
    label: "Rosters In Progress",
    icon: faUsers,
    external: "rostersInProgress",
    cardBg: "#ecfeff",
    iconBg: "#a5f3fc",
    iconColor: "#0891b2",
    textColor: "#0891b2",
  },
  {
    label: "Reports",
    icon: faChartPie,
    path: "/CcpSchoolReports",
    cardBg: "#fff1f2",
    iconBg: "#fecdd3",
    iconColor: "#e11d48",
    textColor: "#e11d48",
  },
];

const CcpSchoolDash = () => {
  const { schoolId } = useContext(SchoolContext);
  const navigate = useNavigate();
  const { data: appsTestingData } = useAppsTesting(schoolId);
  const { data: restrictedReportsData } = useRestrictedReportsHook(schoolId);

  const externalLinks = {
    applicationApproval:
      appsTestingData.length > 0 ? appsTestingData[0].report_1_link : "",
    rostersInProgress:
      restrictedReportsData.length > 0
        ? restrictedReportsData[0].rosters_in_progress
        : "",
  };

  return (
    <Grid container spacing={3} sx={{ padding: 3 }}>
      {/* Quick Link Widget Cards */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {quickLinks.map((item) => {
            const externalLink = item.external
              ? externalLinks[item.external]
              : "";
            const isClickable = item.external
              ? Boolean(externalLink)
              : Boolean(item.path);

            return (
              <Card
                key={item.label}
                onClick={() => {
                  if (item.external) {
                    if (externalLink) {
                      window.open(
                        externalLink,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
                sx={{
                  width: 150,
                  backgroundColor: item.cardBg,
                  borderRadius: 3,
                  boxShadow: "none",
                  border: "none",
                  cursor: isClickable ? "pointer" : "default",
                  transition: "transform 0.15s, box-shadow 0.15s",
                  "&:hover": isClickable
                    ? { transform: "translateY(-3px)", boxShadow: 3 }
                    : {},
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 2,
                    px: 1,
                    gap: 1,
                    "&:last-child": { pb: 2 },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: item.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={item.icon}
                      style={{ fontSize: "1.25rem", color: item.iconColor }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: item.textColor,
                      textAlign: "center",
                      lineHeight: 1.3,
                    }}
                  >
                    {item.label}
                  </Typography>
                  {!item.external && !item.path && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.disabled", fontSize: "0.65rem" }}
                    >
                      Coming Soon
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Resources />
      </Grid>
      <Grid item xs={12} md={12}>
        <Announcements />
      </Grid>
      <Grid item xs={12} md={4}>
        {schoolId ? (
          <CoordinatorOfPathways schoolId={schoolId} />
        ) : (
          <Card>
            <CardContent>
              <Typography variant="body1">Loading...</Typography>
            </CardContent>
          </Card>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        {schoolId ? (
          <CoordinatorOfOperations schoolId={schoolId} />
        ) : (
          <Card>
            <CardContent>
              <Typography variant="body1">Loading...</Typography>
            </CardContent>
          </Card>
        )}
      </Grid>
    </Grid>
  );
};

export default CcpSchoolDash;
